import React, { useRef } from 'react';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBath,
  faRuler,
  faHeart,
  faChevronLeft,
  faChevronRight,
  faMapMarkerAlt,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import './PropertyCarousel.css';
import { Property } from '../../types/property';
import { isPropertyAddress, safeFormatPrice, getPropertyId, getCity, getState } from '../../types/propertyUtils';

interface PropertyCarouselProps {
  properties: Property[];
  selectedProperty?: Property | null;
  onPropertySelect: (property: Property) => void;
  onPropertyRemove?: (property: Property) => void;
  onPropertyHover?: (property: Property | null) => void;
  onClose?: () => void;
  title?: string;
}

const PropertyCarousel: React.FC<PropertyCarouselProps> = ({
  properties,
  selectedProperty,
  onPropertySelect,
  onPropertyRemove,
  onPropertyHover,
  onClose,
  title
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeImageIndex, setActiveImageIndex] = React.useState<{ [key: string]: number }>({});

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleImageNavigation = (propertyId: string, direction: 'prev' | 'next', totalImages: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const currentIndex = activeImageIndex[propertyId] || 0;
    let newIndex: number;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % totalImages;
    } else {
      newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
    }
    
    setActiveImageIndex(prev => ({ ...prev, [propertyId]: newIndex }));
  };

  if (properties.length === 0) return null;

  return (
    <div className="property-carousel-container">
      <div className="carousel-wrapper">
        <Button
          variant="dark"
          className="carousel-nav-btn left"
          onClick={scrollLeft}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        
        <div className="property-carousel" ref={scrollRef}>
          {properties.map((property) => (
            <Card
              key={property.mlsNumber}
              className={`property-carousel-card ${
                selectedProperty?.mlsNumber === property.mlsNumber ? 'selected' : ''
              }`}
              onClick={() => onPropertySelect(property)}
              onMouseEnter={() => onPropertyHover?.(property)}
              onMouseLeave={() => onPropertyHover?.(null)}
            >
              <div className="property-image">
                <img
                  src={
                    property.thumbnails && property.thumbnails.length > 0
                      ? property.thumbnails[activeImageIndex[getPropertyId(property)] || 0]
                      : property.images && property.images.length > 0
                        ? property.images[activeImageIndex[getPropertyId(property)] || 0]
                        : 'https://via.placeholder.com/300x200/1f2937/ffffff?text=No+Image'
                  }
                  alt={isPropertyAddress(property.address) ? property.address.street : (property.address || 'Property')}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    const propId = getPropertyId(property);
                    // Fallback to full image if thumbnail fails
                    if (property.images && property.images.length > 0 &&
                        target.src !== property.images[activeImageIndex[propId] || 0]) {
                      target.src = property.images[activeImageIndex[propId] || 0];
                    } else {
                      target.src = 'https://via.placeholder.com/300x200/1f2937/ffffff?text=No+Image';
                    }
                  }}
                />
                {/* Image navigation arrows */}
                {((property.thumbnails && property.thumbnails.length > 1) || (property.images && property.images.length > 1)) && (
                  <div className="image-navigation">
                    <button
                      className="image-nav-btn prev"
                      onClick={(e) => handleImageNavigation(getPropertyId(property), 'prev',
                        property.thumbnails?.length || property.images?.length || 0, e)}
                      aria-label="Previous image"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="image-nav-btn next"
                      onClick={(e) => handleImageNavigation(getPropertyId(property), 'next',
                        property.thumbnails?.length || property.images?.length || 0, e)}
                      aria-label="Next image"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    <div className="image-counter">
                      {(activeImageIndex[getPropertyId(property)] || 0) + 1} / {property.thumbnails?.length || property.images?.length || 0}
                    </div>
                  </div>
                )}
                {property.daysOnMarket && property.daysOnMarket < 3 && (
                  <div className="badge-new">NEW</div>
                )}
                <div className="property-stats-overlay">
                  <span><FontAwesomeIcon icon={faBed} /> {property.bedrooms}</span>
                  <span><FontAwesomeIcon icon={faBath} /> {property.bathrooms}</span>
                  {property.sqft && property.sqft > 0 && (
                    <span><FontAwesomeIcon icon={faRuler} /> {Math.round(property.sqft / 100) / 10}k</span>
                  )}
                </div>
                {onPropertyRemove && (
                  <Button
                    variant="light"
                    size="sm"
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPropertyRemove(property);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </div>
              
              <Card.Body>
                <h6 className="property-price">{safeFormatPrice(property.price)}</h6>
                <p className="property-address">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                  {getCity(property)}, {getState(property)}
                </p>
                <div className="property-footer">
                  {(property.listingOffice || property.listingAgent?.officeName) && (
                    <small className="text-muted">
                      Courtesy of {property.listingOffice || property.listingAgent?.officeName}
                    </small>
                  )}
                  <small className="text-muted">
                    MLS# {property.mlsNumber}
                    {property.daysOnMarket !== undefined && property.daysOnMarket > 0 && (
                      <span> • {property.daysOnMarket} days</span>
                    )}
                  </small>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
        
        <Button
          variant="dark"
          className="carousel-nav-btn right"
          onClick={scrollRight}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>
    </div>
  );
};

export default PropertyCarousel;